import { Link } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import { contactEmail, siteName, siteUrl } from '@/mocks/melons';

const pillars = [
  {
    icon: 'ri-shield-check-line',
    title: '真实可溯',
    desc: '所有瓜料都以已公开的信息为准，尽量给出时间点与多方说法，标注「网传待核实」内容，不做添油加醋的二次创作。',
  },
  {
    icon: 'ri-time-line',
    title: '省时提效',
    desc: '用瓜田盘点与时间线把碎片信息串起来，一篇读完就能补齐一个事件的前因后果，不必挨个刷动态、反复对热搜。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '理性克制',
    desc: '围观的边界是尊重。我们只整理有公共讨论度的内容，不挖掘、不扩散当事人的私人隐私，坚持理性吃瓜。',
  },
];

const principles = [
  '先整理、再呈现：每条瓜料都标注事件时间与信息脉络，尽量呈现多方说法。',
  '不追热点蹭流量：只收录具备完整信息、值得反复回看的瓜田盘点与精品瓜。',
  '对传闻保持克制：缺乏公开佐证的信息一律标注待核实，不做标题党式的猜测。',
  '分类清晰可持续：按栏目沉淀，方便你按主题回看，也方便编辑部长期维护。',
];

const voices = [
  { name: '阿黎', tag: '吃瓜三年', text: '以前刷动态刷到半夜都补不全进度，现在看瓜田盘点一篇就接上了，真的省事。' },
  { name: '北漂老铁', tag: '早晚通勤', text: '排版舒服、节奏快，把反转讲得清楚，不会像别的帖子那样看半天不知道在说啥。' },
  { name: '深夜小林', tag: '内容创作者', text: '最喜欢时间线和关键信息那块，盘点做得有条理，对找选题素材也很有帮助。' },
];

export default function About() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: `${siteUrl}/about` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: `关于${siteName}`,
      url: `${siteUrl}/about`,
      description: `以瓜田盘点为主线，系统整理快手吃瓜瓜料、精品瓜与吃瓜合集的图文资讯站，坚持真实、省时、理性。`,
      mainEntity: {
        '@type': 'Organization',
        name: siteName,
        url: siteUrl,
      },
    },
  ];

  return (
    <>
      <Seo
        title={`关于我们 - 快手吃瓜瓜料大全 · 品牌介绍与内容理念`}
        description="了解快手吃瓜瓜料大全的网站定位、发展理念、内容原则与用户口碑。我们以瓜田盘点为主线，整理快手吃瓜精品瓜与吃瓜合集，坚持真实、省时、理性吃瓜。"
        keywords="快手吃瓜,关于我们,瓜田盘点,内容理念,吃瓜合集"
        canonicalUrl={`${siteUrl}/about`}
        type="website"
        jsonLd={jsonLd}
      />

      <nav className="border-b border-background-200 bg-background-50" aria-label="面包屑">
        <div className="container-page flex items-center gap-2 py-3 text-[13px] text-foreground-500">
          <a href="/" className="inline-flex items-center gap-1 hover:text-primary-700"><i className="ri-home-4-line" aria-hidden="true" />首页</a>
          <i className="ri-arrow-right-s-line" aria-hidden="true" />
          <span className="text-foreground-700">关于我们</span>
        </div>
      </nav>

      <section className="relative overflow-hidden bg-background-50 py-14 md:py-20">
        <div
          className="pointer-events-none absolute -left-24 top-0 h-80 w-80 rounded-full opacity-50"
          style={{ background: 'radial-gradient(closest-side, oklch(var(--primary-100)), transparent)' }}
          aria-hidden="true"
        />
        <div className="container-page relative max-w-4xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium tracking-wide text-primary-700">
            <i className="ri-user-smile-line" aria-hidden="true" />
            关于我们
          </span>
          <h1 className="mt-5 font-heading text-3xl font-black leading-[1.2] text-foreground-950 md:text-5xl md:leading-[1.15]">
            让每个人都能<span className="text-primary-600">高效吃瓜</span>
            <br />
            是我们做这件事的初心
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-foreground-700 md:text-lg">
            {siteName}是一份以快手吃瓜瓜田盘点为主线的图文资讯集合。我们把散落在不同动态里的瓜料，按事件与时间线重新整理成瓜田盘点、精品瓜与吃瓜合集，让你不必挨个刷屏，也能快速补齐必吃瓜。
          </p>
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100 py-12 md:py-16">
        <div className="container-page grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">我们是谁 &amp; 网站定位</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              我们不是「追热点的营销号」，而是把内容当产品来维护的资讯编辑团队。定位很朴素：做一块内容清晰、能反复回看的瓜田。
            </p>
          </div>
          <div className="flex flex-col gap-4 lg:col-span-2">
            {pillars.map((p) => (
              <div key={p.title} className="flex items-start gap-4 rounded-2xl border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-600 text-xl text-background-50">
                  <i className={p.icon} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="text-base font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-50 py-12 md:py-16">
        <div className="container-page grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-secondary-200 bg-secondary-100 px-3 py-1 text-xs font-medium tracking-wide text-secondary-900">
              <i className="ri-check-double-line" aria-hidden="true" />
              内容原则
            </span>
            <h2 className="mt-4 text-2xl font-bold text-foreground-950 md:text-[34px]">我们坚持这些底线</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              内容原则决定了一块瓜田能走多远。我们把对读者负责放在流量前面，用流程保证每一篇盘点都经得起回看。
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="flex flex-col gap-3">
              {principles.map((pr, i) => (
                <li key={i} className="flex items-start gap-3 rounded-xl border border-background-200 bg-background-100 px-5 py-4 text-sm leading-relaxed text-foreground-800">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-500 font-heading text-xs font-bold text-foreground-950">
                    {i + 1}
                  </span>
                  {pr}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100 py-12 md:py-16">
        <div className="container-page">
          <div className="flex flex-col items-start gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-100 px-3 py-1 text-xs font-medium tracking-wide text-accent-900">
              <i className="ri-chat-3-line" aria-hidden="true" />
              用户口碑
            </span>
            <h2 className="text-2xl font-bold text-foreground-950 md:text-[34px]">瓜友们怎么说</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {voices.map((v) => (
              <figure key={v.name} className="flex flex-col justify-between rounded-2xl border border-background-200 bg-background-50 p-6">
                <blockquote className="text-[15px] leading-relaxed text-foreground-700">“{v.text}”</blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                    {v.name[0]}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-foreground-900">{v.name}</p>
                    <p className="text-xs text-foreground-500">{v.tag}</p>
                  </div>
                  <span className="ml-auto inline-flex items-center gap-0.5 text-accent-600">
                    <i className="ri-star-s-fill" aria-hidden="true" />
                    <i className="ri-star-s-fill" aria-hidden="true" />
                    <i className="ri-star-s-fill" aria-hidden="true" />
                    <i className="ri-star-s-fill" aria-hidden="true" />
                    <i className="ri-star-s-fill" aria-hidden="true" />
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="border-t border-background-200 bg-background-50 py-12 md:py-16">
        <div className="container-page grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium tracking-wide text-primary-700">
              <i className="ri-mail-send-line" aria-hidden="true" />
              联系我们
            </span>
            <h2 className="mt-4 text-2xl font-bold text-foreground-950 md:text-[32px]">投稿、合作或聊两句？</h2>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-foreground-600">
              欢迎瓜友提供线索与时间线补充，投稿请尽量附上可核对的公开链接与时间节点，编辑部会优先核实并整理进对应栏目。
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-3 rounded-xl border border-background-200 bg-background-100 px-5 py-3.5 transition-colors hover:border-primary-300">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-600 text-lg text-background-50">
                  <i className="ri-mail-line" aria-hidden="true" />
                </span>
                <span className="text-sm">
                  <span className="block text-xs text-foreground-500">投稿与合作邮箱</span>
                  <span className="font-semibold text-foreground-900">{contactEmail}</span>
                </span>
              </a>
              <Link to="/#faq" className="inline-flex items-center gap-3 rounded-xl border border-background-200 bg-background-100 px-5 py-3.5 transition-colors hover:border-primary-300">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-500 text-lg text-foreground-950">
                  <i className="ri-question-line" aria-hidden="true" />
                </span>
                <span className="text-sm">
                  <span className="block text-xs text-foreground-500">关于投稿与瓜料来源</span>
                  <span className="font-semibold text-foreground-900">常见问题与指南</span>
                </span>
              </Link>
            </div>
          </div>
          <div className="flex flex-col justify-center gap-4 rounded-2xl bg-foreground-950 p-8 md:p-10">
            <h3 className="text-2xl font-bold text-background-50">回到瓜田，接着补今天的瓜</h3>
            <p className="text-sm leading-relaxed text-background-200">
              首页热榜每 30 分钟更新，精品瓜与瓜田盘点持续沉淀。错过的进度，随时可以回来一次补齐。
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              <a href="/#hot" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400">
                <i className="ri-fire-fill" aria-hidden="true" />看今日热榜
              </a>
              <a href="/" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-200/40 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-100 hover:text-foreground-900">
                回首页
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}